import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

const positions = new Map<string, number>();

/** New pages start at top, back/forward restores, hashes scroll to their section. */
export default function ScrollManager() {
  const { key, hash, pathname } = useLocation();
  const navType = useNavigationType();
  const keyRef = useRef(key);

  useEffect(() => {
    if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';
    const onScroll = () => { positions.set(keyRef.current, window.scrollY); };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useLayoutEffect(() => {
    keyRef.current = key;
    let raf = 0;
    let tries = 0;
    const saved = positions.get(key);

    if (hash) {
      // Wait two frames so pinned sections have built their spacers before measuring.
      const seek = () => {
        const el = document.getElementById(decodeURIComponent(hash.slice(1)));
        if (el && tries > 1) {
          window.scrollTo(0, el.getBoundingClientRect().top + window.scrollY);
          return;
        }
        tries += 1;
        if (tries < 30) raf = requestAnimationFrame(seek);
      };
      raf = requestAnimationFrame(seek);
    } else if (navType === 'POP' && saved !== undefined) {
      const restore = () => {
        tries += 1;
        if (tries < 3) { raf = requestAnimationFrame(restore); return; }
        window.scrollTo(0, saved);
      };
      raf = requestAnimationFrame(restore);
    } else {
      window.scrollTo(0, 0);
    }
    return () => cancelAnimationFrame(raf);
  }, [key, hash, pathname, navType]);

  return null;
}